import { Controller } from "@hotwired/stimulus"

/**
 * Toast Manager Controller — "latest only" throttling.
 *
 * Shows at most 1 toast at a time. When a new toast arrives while
 * one is visible, the old one is dismissed first, then the new one
 * enters after the exit animation completes.
 */
export default class extends Controller {
  static targets = ["toast"]
  static values = {
    delay: { type: Number, default: 3000 },
    animationDuration: { type: Number, default: 300 }
  }

  connect() {
    this.dismissTimer = null
    this.isAnimating = false
    this.pendingToast = null

    // Watch for new toasts appended via Turbo Stream broadcasts
    this.observer = new MutationObserver(this.handleMutation.bind(this))
    this.observer.observe(this.element, { childList: true })

    // If there's already a toast (server-rendered flash), start its timer
    if (this.hasToastTarget) {
      this.showToast(this.toastTarget)
    }
  }

  disconnect() {
    this.observer?.disconnect()
    this.clearTimer()
    this.pendingToast = null
  }

  handleMutation(mutations) {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue

        const toast = node.hasAttribute("data-toast-manager-target")
          ? node
          : node.querySelector("[data-toast-manager-target]")

        if (toast) {
          this.handleNewToast(toast)
        }
      }
    }
  }

  handleNewToast(newToastElement) {
    const existingToast = this.toastTargets.find(t => t !== newToastElement)

    if (existingToast) {
      // Queue the new toast, dismiss old first
      this.pendingToast = newToastElement
      this.dismissOld(existingToast)
    } else if (this.isAnimating) {
      // Mid-dismiss animation — replace whatever is queued
      this.pendingToast = newToastElement
    } else {
      this.showToast(newToastElement)
    }
  }

  dismissOld(oldToast) {
    this.isAnimating = true
    this.clearTimer()
    oldToast.classList.remove("animate-toast-enter")
    oldToast.classList.add("animate-toast-exit")

    setTimeout(() => {
      this.removeToastAndWrapper(oldToast)
      this.isAnimating = false
      this.showPending()
    }, 200)
  }

  showPending() {
    if (!this.pendingToast) return

    const toast = this.pendingToast
    this.pendingToast = null
    this.showToast(toast)
  }

  showToast(toastElement) {
    toastElement.classList.add("animate-toast-enter")
    this.startDismissTimer()
  }

  startDismissTimer() {
    this.clearTimer()
    this.dismissTimer = setTimeout(() => this.dismiss(), this.delayValue)
  }

  clearTimer() {
    if (this.dismissTimer) {
      clearTimeout(this.dismissTimer)
      this.dismissTimer = null
    }
  }

  dismiss() {
    if (!this.hasToastTarget) return
    this.clearTimer()
    this.isAnimating = true

    const toast = this.toastTarget
    toast.classList.remove("animate-toast-enter")
    toast.classList.add("animate-toast-exit")

    setTimeout(() => {
      this.removeToastAndWrapper(toast)
      this.isAnimating = false
      this.showPending()
    }, this.animationDurationValue)
  }

  // Remove the toast and its wrapper div (the direct child of the controller element)
  removeToastAndWrapper(toast) {
    let node = toast
    while (node.parentElement && node.parentElement !== this.element) {
      node = node.parentElement
    }
    node.remove()
  }
}
